import React, { useState, useEffect } from "react";
import {
  Opportunity,
  ProposalAddendum,
  Requirement,
  UserProfile,
  MilestonePresets,
} from "../../types";
import {
  subscribeToAddenda,
  createAddendum,
  updateAddendum,
  deleteAddendum,
  toggleAddendumAcknowledgment,
} from "../../services/addendaService";
import { AddendumModal } from "./AddendumModal";
import { Modal } from "../common/Modal";
import {
  FileText,
  Plus,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Trash2,
  Edit2,
  Layers,
} from "lucide-react";
import { getDocumentDownloadUrl } from "../../services/storageService";

interface AddendaLogProps {
  opportunity: Opportunity;
  requirements: Requirement[];
  user?: UserProfile | null;
  milestonePresets?: MilestonePresets;
}

export const AddendaLog: React.FC<AddendaLogProps> = ({
  opportunity,
  requirements,
  user,
  milestonePresets,
}) => {
  const [addenda, setAddenda] = useState<ProposalAddendum[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAddendum, setSelectedAddendum] = useState<ProposalAddendum | null>(null);
  const [addendumToDelete, setAddendumToDelete] = useState<ProposalAddendum | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = subscribeToAddenda(opportunity.id, (items) => {
      setAddenda(items);
    });
    return () => unsub();
  }, [opportunity.id]);

  const handleOpenAddModal = () => {
    setSelectedAddendum(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item: ProposalAddendum) => {
    setSelectedAddendum(item);
    setIsModalOpen(true);
  };

  const handleSaveAddendum = async (
    data: Omit<ProposalAddendum, "id" | "opportunityId" | "createdAt" | "updatedAt">,
    autoShiftDates: boolean,
  ) => {
    if (selectedAddendum) {
      await updateAddendum(
        opportunity.id,
        selectedAddendum.id,
        data,
        selectedAddendum.number,
        selectedAddendum.affectedRequirementIds || [],
      );
    } else {
      await createAddendum(opportunity.id, data, user || {}, {
        autoShiftOpportunityDates: autoShiftDates,
        milestonePresets,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!addendumToDelete) return;
    const item = addendumToDelete;
    setAddendumToDelete(null);
    await deleteAddendum(opportunity.id, item.id, item.number, item.affectedRequirementIds || []);
  };

  const handleToggleAcknowledgment = async (item: ProposalAddendum) => {
    const nextState = item.acknowledgmentStatus !== "acknowledged";
    await toggleAddendumAcknowledgment(opportunity.id, item.id, nextState, user || {});
  };

  const handleDownloadDoc = async (item: ProposalAddendum) => {
    if (!item.documentPath) return;
    setDownloadingDocId(item.id);
    try {
      const url = await getDocumentDownloadUrl(item.documentPath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Failed to download addendum document:", err);
    } finally {
      setDownloadingDocId(null);
    }
  };

  // KPI calculations
  const totalCount = addenda.length;
  const pendingAckCount = addenda.filter(
    (a) => a.acknowledgmentStatus === "pending_acknowledgment",
  ).length;
  const deadlineShiftsCount = addenda.filter(
    (a) => a.shiftsSubmissionDeadline || a.shiftsQuestionsDeadline,
  ).length;

  return (
    <div className="space-y-6">
      {/* Top Action & KPI Bar */}
      <div className="grid gap-3 sm:grid-cols-3">
        {/* KPI: Total Addenda */}
        <div className="glass rounded-xl border border-slate-200/80 p-4 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Addenda Logged
            </span>
            <Layers className="h-4 w-4 text-[#2ba5b5]" />
          </div>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">{totalCount}</p>
          <span className="text-[11px] text-slate-500">Client amendments & bulletins</span>
        </div>

        {/* KPI: Acknowledgment Compliance */}
        <div className="glass rounded-xl border border-slate-200/80 p-4 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Form Compliance
            </span>
            {pendingAckCount > 0 ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            )}
          </div>
          <p
            className={`mt-2 text-2xl font-bold ${
              pendingAckCount > 0
                ? "text-amber-600 dark:text-amber-400"
                : "text-slate-900 dark:text-slate-100"
            }`}
          >
            {pendingAckCount > 0 ? `${pendingAckCount} Pending` : "100% Signed"}
          </p>
          <span className="text-[11px] text-slate-500">
            {pendingAckCount > 0 ? "Signed acknowledgment required" : "All required forms signed"}
          </span>
        </div>

        {/* KPI: Deadline Extensions */}
        <div className="glass rounded-xl border border-slate-200/80 p-4 shadow-xs dark:border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Deadline Extensions
            </span>
            <Calendar className="h-4 w-4 text-blue-500" />
          </div>
          <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
            {deadlineShiftsCount}
          </p>
          <span className="text-[11px] text-slate-500">
            {opportunity.dueDate
              ? `Current Due Date: ${opportunity.dueDate}`
              : "No deadline shifts"}
          </span>
        </div>
      </div>

      {/* Header with Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
            RFP Amendments & Scope Changes
          </h3>
          <p className="text-xs text-slate-500">
            Track official client bulletins, date shifts, and link modifications to requirements.
          </p>
        </div>

        <button
          onClick={handleOpenAddModal}
          className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238997]"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Log Addendum</span>
        </button>
      </div>

      {/* Addenda List or Empty State */}
      {addenda.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center dark:border-slate-700">
          <FileText className="mx-auto h-8 w-8 text-slate-400" />
          <h4 className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
            No Addenda Logged Yet
          </h4>
          <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
            When the buyer releases amendments, Q&A responses, or deadline extensions, log them here
            to keep the team aligned.
          </p>
          <button
            onClick={handleOpenAddModal}
            className="btn-animate mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Log First Addendum</span>
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {addenda.map((item) => {
            const affectedReqs = requirements.filter((r) =>
              item.affectedRequirementIds?.includes(r.id),
            );

            return (
              <div
                key={item.id}
                className="glass rounded-xl border border-slate-200/90 p-4 transition-all hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700 shadow-xs"
              >
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="rounded-md bg-[#2ba5b5]/15 px-2.5 py-0.5 text-xs font-bold text-[#1b7582] dark:bg-[#2ba5b5]/25 dark:text-[#52c8d7]">
                      {item.number}
                    </span>
                    <h4 className="text-xs font-bold text-slate-900 dark:text-slate-100">
                      {item.title}
                    </h4>
                    <span className="flex items-center gap-1 text-[11px] text-slate-500">
                      <Calendar className="h-3 w-3" />
                      Issued: {item.issueDate}
                    </span>
                  </div>

                  {/* Actions & Status Pill */}
                  <div className="flex items-center gap-2">
                    {/* Acknowledgment Toggle Button */}
                    {item.acknowledgmentRequired ? (
                      <button
                        onClick={() => handleToggleAcknowledgment(item)}
                        title="Click to toggle acknowledgment status"
                        className={`btn-animate inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition-colors ${
                          item.acknowledgmentStatus === "acknowledged"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800"
                            : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800"
                        }`}
                      >
                        {item.acknowledgmentStatus === "acknowledged" ? (
                          <>
                            <CheckCircle2 className="h-3 w-3" />
                            <span>Signed & Acknowledged</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-3 w-3" />
                            <span>Pending Signed Form</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        No Ack Required
                      </span>
                    )}

                    {/* Edit Button */}
                    <button
                      onClick={() => handleOpenEditModal(item)}
                      title="Edit addendum"
                      className="rounded p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={() => setAddendumToDelete(item)}
                      title="Delete addendum"
                      className="rounded p-1 text-slate-400 hover:text-rose-600 hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-rose-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Deadline Shift Alert Box (if applicable) */}
                {(item.shiftsSubmissionDeadline || item.shiftsQuestionsDeadline) && (
                  <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-blue-50/70 p-2.5 text-xs text-blue-900 border border-blue-100 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900/40">
                    <Clock className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                    <div className="flex items-center gap-3 flex-wrap">
                      {item.shiftsSubmissionDeadline && (
                        <span>
                          <strong className="font-semibold">Submission Extended:</strong>{" "}
                          {item.newSubmissionDate} {item.newSubmissionTime || ""}
                        </span>
                      )}
                      {item.shiftsQuestionsDeadline && (
                        <span>
                          <strong className="font-semibold">Questions Extended:</strong>{" "}
                          {item.newQuestionsDate}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Summary Text */}
                <p className="mt-2.5 text-xs text-slate-700 leading-relaxed dark:text-slate-300">
                  {item.summary}
                </p>

                {/* Footer: Tagged Requirements & Document */}
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 border-t border-slate-100 dark:border-slate-800/80">
                  {/* Impacted Requirements chips */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-medium text-slate-500">
                      Impacted Requirements:
                    </span>
                    {affectedReqs.length === 0 ? (
                      <span className="text-[11px] text-slate-400 italic">None tagged</span>
                    ) : (
                      affectedReqs.map((req) => (
                        <span
                          key={req.id}
                          className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {req.sectionRef ? `${req.sectionRef} ` : ""}
                          {req.title}
                        </span>
                      ))
                    )}
                  </div>

                  {/* Document Attachment Button */}
                  {item.documentPath && (
                    <button
                      onClick={() => handleDownloadDoc(item)}
                      disabled={downloadingDocId === item.id}
                      className="btn-animate inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ba5b5] hover:text-[#238997] dark:text-[#52c8d7]"
                    >
                      <Download className="h-3 w-3" />
                      <span>{item.documentName || "View Attachment"}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Addendum Modal */}
      <AddendumModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        opportunity={opportunity}
        addendum={selectedAddendum}
        requirements={requirements}
        milestonePresets={milestonePresets}
        onSave={handleSaveAddendum}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(addendumToDelete)}
        onClose={() => setAddendumToDelete(null)}
        title="Confirm Addendum Deletion"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Are you sure you want to delete{" "}
            <strong className="text-slate-900 dark:text-slate-100">
              {addendumToDelete?.number}
            </strong>
            ? Any linked requirement tags will be cleaned up automatically.
          </p>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={() => setAddendumToDelete(null)}
              className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmDelete}
              className="btn-animate rounded-lg bg-rose-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-rose-700"
            >
              Delete Addendum
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
